'use client'

import s from './Navbar.module.scss'
import Link from 'next/link'
import Image from 'next/image'
import React, { useState, useEffect, useCallback } from 'react'
import NavItem from './NavItem'
import NavItemWithSubmenu from './NavItemWithSubmenu'
import { classnames } from '@/utils/classnames'
import { usePathname } from 'next/navigation'
import { UserButton } from '@clerk/nextjs'
import { BurgerIcon, CloseIcon } from '@/components/icon/icons'
import { ButtonOrLink } from '@/components/ButtonOrLink'
import { AdminThemeToggle } from '@/components/ThemeToggle/AdminThemeToggle'
import { useNavbarLogoSrc } from '@/hooks/useZebraLogoSrc'
import { useNavbarConfig } from '@/config/navigation/useNavbarConfig'

export const Navbar = () => {
  const zebraLogoSrc = useNavbarLogoSrc()
  const [navActive, setNavActive] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const pathname = usePathname()
  const normalize = (path: string) => path.replace(/\/+$/, '') || '/'
  const isHomePage = normalize(pathname) === '/'

  const handleLogoClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      setNavActive(false)
      if (isHomePage) {
        e.preventDefault()
        window.scrollTo({ top: 0, behavior: 'smooth' })
      }
    },
    [isHomePage],
  )

  const { menuList, authButton } = useNavbarConfig()

  useEffect(() => {
    const handleScrollResize = () => {
      const isDesktop = window.innerWidth >= 1024
      const pastThreshold = window.scrollY > 96 // 6rem
      setScrolled(isDesktop && pastThreshold)
    }

    window.addEventListener('scroll', handleScrollResize)
    window.addEventListener('resize', handleScrollResize)
    handleScrollResize()

    return () => {
      window.removeEventListener('scroll', handleScrollResize)
      window.removeEventListener('resize', handleScrollResize)
    }
  }, [])

  useEffect(() => {
    if (navActive) {
      document.body.classList.add('blockScroll')
    } else {
      document.body.classList.remove('blockScroll')
    }

    return () => {
      document.body.classList.remove('blockScroll')
    }
  }, [navActive])

  if (!authButton) return null

  return (
    <header className={`${s.header} ${scrolled ? s.scrolled : ''}`}>
      <nav className={s.container}>
        <div className={s.leftSection}>
          <Link href="/" className={s.logoLink} onClick={handleLogoClick}>
            <Image
              src={zebraLogoSrc}
              alt="Zebra Logo"
              width={120}
              height={35}
              className={s.logo}
              style={{ width: '120px', height: '35px' }}
              priority
            />
          </Link>

          <div className={s.themeToggleMobile}>
            <AdminThemeToggle forMobileNavbar />
          </div>

          <div className={s.menubar}>
            <BurgerIcon
              width={16}
              height={16}
              className={navActive ? s.iconHidden : s.iconVisible}
              onClick={() => setNavActive(!navActive)}
            />
            <CloseIcon
              width={16}
              height={16}
              className={navActive ? s.iconVisible : s.iconHidden}
              onClick={() => setNavActive(!navActive)}
            />
          </div>

          <div className={classnames(s.menulist, { [s.active]: navActive })}>
            <div className={s.menulistScroll}>
              {menuList.map((menu) => {
                const hasActiveChild = menu.submenu
                  ? menu.submenu.some((item) =>
                      normalize(pathname).startsWith(`/${item.handle}`),
                    )
                  : false

                const isActive =
                  menu.handle === '/'
                    ? normalize(pathname) === '/'
                    : normalize(pathname) === normalize(menu.href)

                return (
                  <div key={menu.handle}>
                    {menu.submenu ? (
                      <NavItemWithSubmenu
                        active={isActive}
                        hasActiveChild={hasActiveChild}
                        text={menu.text}
                        href={menu.href}
                        submenu={menu.submenu}
                        onSubmenuClick={() => setNavActive(false)}
                      />
                    ) : (
                      <div onClick={() => setNavActive(false)}>
                        <NavItem
                          active={isActive}
                          {...menu}
                          onCloseNav={() => setNavActive(false)}
                        />
                      </div>
                    )}
                  </div>
                )
              })}

              {/* Przycisk zaloguj się w mobile */}
              <div
                className={s.mobileLoginButton}
                onClick={() => setNavActive(false)}
              >
                <ButtonOrLink
                  as="link"
                  href={authButton.href}
                  variant="black"
                  iconRight="ArrowIcon"
                >
                  {authButton.text}
                </ButtonOrLink>
              </div>
            </div>
          </div>
        </div>

        <div className={s.rightSection}>
          <div className={s.themeToggleDesktop}>
            <AdminThemeToggle />
          </div>
          <div className={s.loginButton} onClick={() => setNavActive(false)}>
            <ButtonOrLink
              as="link"
              href={authButton.href}
              variant="black"
              iconRight="Arrow2Icon"
            >
              {authButton.text}
            </ButtonOrLink>
          </div>
          {/* <LanguageSwitcher /> */}
        </div>
      </nav>
    </header>
  )
}
